/**
 * @license
 *
 * Copyright IBM Corp. 2020
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
export { default as CustomElement } from '../../components/video-player/video-player-composite.js';
import createReactCustomElementType, {
  booleanSerializer,
  numberSerializer,
} from '@carbon/web-components/es/globals/wrappers/createReactCustomElementType.js';
import PropTypes from 'prop-types';
import settings from 'carbon-components/es/globals/js/settings.js';
import ddsSettings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
var prefix = settings.prefix;
var ddsPrefix = ddsSettings.stablePrefix;
export var descriptor = {
  autoPlay: {
    serialize: booleanSerializer,
    attribute: 'auto-play',
  },
  embeddedVideos: {
    attribute: false,
  },
  caption: {
    attribute: 'caption',
  },
  customVideoDescription: {
    attribute: 'video-description',
  },
  formatCaption: {
    attribute: false,
  },
  formatDuration: {
    attribute: false,
  },
  hideCaption: {
    serialize: booleanSerializer,
    attribute: 'hide-caption',
  },
  backgroundMode: {
    serialize: booleanSerializer,
    attribute: 'background-mode',
  },
  mediaData: {
    attribute: false,
  },
  videoId: {
    attribute: 'video-id',
  },
  aspectRatio: {
    attribute: 'aspect-ratio',
  },
  isPlaying: {},
  playingMode: {
    attribute: 'playing-mode',
  },
  thumbnail: {
    attribute: 'thumbnail',
  },
  videoThumbnailWidth: {
    serialize: numberSerializer,
    attribute: 'video-thumbnail-width',
  },
  onContentStateChange: {
    event: ''.concat(ddsPrefix, '-video-player-content-state-changed'),
  },
  onPlaybackStateChange: {
    event: ''.concat(ddsPrefix, '-video-player-playback-state-changed'),
  },
};
export var propTypes = {
  /**
   * `true` to autoplay the videos.
   */
  autoPlay: PropTypes.bool,
  /**
   * The embedded Kaltura player element (that has `.sendNotification()`, etc. APIs), keyed by the video ID.
   */
  embeddedVideos: PropTypes.string,
  /**
   * Optional custom video caption.
   */
  caption: PropTypes.string,
  /**
   * Custom video description. This property should only be set when `playing-mode="lightbox"`.
   */
  customVideoDescription: PropTypes.string,
  /**
   * The formatter for the video caption, composed with the video name and the video duration.
   * Should be changed upon the locale the UI is rendered with.
   */
  formatCaption: PropTypes.string,
  /**
   * The formatter for the video duration.
   * Should be changed upon the locale the UI is rendered with.
   */
  formatDuration: PropTypes.string,
  /**
   * `true` to hide the caption.
   */
  hideCaption: PropTypes.bool,
  /**
   * `true` to autoplay, mute, and hide player UI.
   */
  backgroundMode: PropTypes.bool,
  /**
   * The video data, keyed by the video ID.
   */
  mediaData: PropTypes.string,
  /**
   * The video ID.
   */
  videoId: PropTypes.string,
  /**
   * The aspect ratio.
   */
  aspectRatio: PropTypes.string,
  /**
   * The current playback state
   */
  isPlaying: PropTypes.string,
  /**
   * The video player's mode showing Inline or Lightbox.
   */
  playingMode: PropTypes.string,
  /**
   * Optional custom video thumbnail
   */
  thumbnail: PropTypes.string,
  /**
   * The video thumbnail width.
   */
  videoThumbnailWidth: PropTypes.number,
  /**
   * The name of the custom event fired after video content state is changed upon a user gesture.
   */
  onContentStateChange: PropTypes.func,
  /**
   * The name of the custom event fired requesting playback state change.
   */
  onPlaybackStateChange: PropTypes.func,
};
const Component = createReactCustomElementType(
  ''.concat(ddsPrefix, '-video-player-composite'),
  descriptor
);
Component.propTypes = propTypes;
export default Component;
